import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type POLineItemPrefill = {
  variant_id: number
  product_id: number
  product_title: string
  variant_title: string
  sku?: string
  barcode?: string
  vendor: string
  unit_cost?: number
  recommended_qty?: number
  on_hand?: number
  incoming?: number
  action_signal?: string
  return_rate?: number
}

export type POFilters = {
  status?: string
  supplier?: string
  location?: string
  search?: string
}

// Default visibility for toggleable columns. Columns not listed (po_number,
// vendor, status, items_count, select/expand/actions) are always visible.
export const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
  // On by default: the Vendor column shows the vendor only, so the supplier
  // would otherwise not appear anywhere on the list.
  supplier: true,
  invoice_number: false,
  supplier_order_number: false,
  created_date: true,
  ordered_date: false,
  location_to_receive: true,
  ship_date: false,
  expected_delivery_date: true,
  total_cost_base: true,
  received_count: true,
  payment_status: false,
  payment_due_date: false,
}

type UIState = {
  filters: POFilters
  // Which surface the Purchase orders page shows: the PO list ('pos') or the
  // backorder / OOS tracking list ('backorder'). Driven by the tab strip.
  activeView: 'pos' | 'backorder'
  selectedPOs: Set<string>
  activeModal: 'create' | 'edit' | 'approval' | 'markOrdered' | 'orderKentucky' | 'tracking' | 'addToPO' | null
  activePO: string | null
  prefillItems: POLineItemPrefill[] | null
  columnVisibility: Record<string, boolean>
  setFilters: (f: Partial<POFilters>) => void
  setActiveView: (v: UIState['activeView']) => void
  clearFilters: () => void
  toggleSelectedPO: (id: string) => void
  toggleSelectAll: (ids: string[]) => void
  clearSelectedPOs: () => void
  setActiveModal: (modal: UIState['activeModal'], poId?: string, prefillItems?: POLineItemPrefill[]) => void
  setColumnVisible: (id: string, visible: boolean) => void
  resetColumnVisibility: () => void
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      filters: {},
      activeView: 'pos',
      selectedPOs: new Set(),
      activeModal: null,
      activePO: null,
      prefillItems: null,
      columnVisibility: { ...DEFAULT_COLUMN_VISIBILITY },

      setFilters: (f) =>
        set((s) => ({ filters: { ...s.filters, ...f } })),

      setActiveView: (v) => set({ activeView: v }),

      clearFilters: () => set({ filters: {} }),

      toggleSelectedPO: (id) =>
        set((s) => {
          const next = new Set(s.selectedPOs)
          if (next.has(id)) {
            next.delete(id)
          } else {
            next.add(id)
          }
          return { selectedPOs: next }
        }),

      toggleSelectAll: (ids) =>
        set((s) => {
          const allSelected = ids.every((id) => s.selectedPOs.has(id))
          return { selectedPOs: allSelected ? new Set() : new Set(ids) }
        }),

      clearSelectedPOs: () => set({ selectedPOs: new Set() }),

      setActiveModal: (modal, poId, prefillItems) =>
        set({ activeModal: modal, activePO: poId ?? null, prefillItems: prefillItems ?? null }),

      setColumnVisible: (id, visible) =>
        set((s) => ({
          columnVisibility: { ...s.columnVisibility, [id]: visible },
        })),

      resetColumnVisibility: () =>
        set({ columnVisibility: { ...DEFAULT_COLUMN_VISIBILITY } }),
    }),
    {
      name: 'po-ui',
      // Only persist column prefs — selection / modal / filter state should
      // not survive page refresh.
      partialize: (s) => ({ columnVisibility: s.columnVisibility }),
      // v1: the Vendor column used to display the supplier, so everyone saw a
      // supplier whether or not the Supplier column was on. Now that Vendor
      // shows the vendor, anyone with a persisted `supplier: false` would see
      // no supplier at all. Turn it on once; later toggles persist normally.
      version: 1,
      migrate: (persisted, version) => {
        const state = persisted as { columnVisibility?: Record<string, boolean> }
        if (version < 1 && state?.columnVisibility) {
          state.columnVisibility = { ...state.columnVisibility, supplier: true }
        }
        return state
      },
    }
  )
)
